// ============================================================
// A VEHICLE'S BASE RING IS ITS FACING RING (plan design-gaps, V1; the ruling's
// "its base ring is its facing ring"): four arcs round the hull - the front,
// the right side, the rear, the left side - carved by the table's arc preset
// (engine/battlefield/facing `ARC_PRESETS`), each with the armour it presents;
// the arc the shooter stands in is lit (the armour the shot will meet, through
// the app's one facing rule, `effectiveVehicleFacing`: a GM's pinned facing,
// else the map's `arcFor`); and a chevron at the bow. A hull that
// has never been turned has no facing to show: no ring.
//
// Ground points, for the renderer to project. Pure. Zero React dependency.
// ============================================================

import {
  ARC_PRESETS,
  arcFor,
  bearingBetween,
  finite,
  headingPoint,
  wrap180,
  type ArcPreset,
} from '../battlefield/facing';
import { effectiveVehicleFacing, type VehicleFacingChoice } from '../vehicleFacing';
import type { Vec3 } from '../battlefield/types';

type FacingId = 'front' | 'right' | 'rear' | 'left';

interface FacingArc {
  id: FacingId;
  /** The armour this side presents (the left and right sides share the side's). */
  armour: number;
  /** The shooter stands in this arc: its armour is the one the shot will meet. */
  lit: boolean;
  /** The arc on the ground, at the ring's radius, from its start to its end. */
  points: Vec3[];
  /** Where its armour is printed: outside the ring, at the arc's middle. */
  label: Vec3;
}

export interface FacingRing {
  arcs: FacingArc[];
  /** The bow chevron: its left foot, its tip, its right foot. */
  bow: [Vec3, Vec3, Vec3];
}

/** How many segments an arc is drawn with, per 90 degrees. */
const STEPS_PER_QUARTER = 8;
/** Where the armour sits, as a share of the radius; the chevron's tip and feet. */
const LABEL_OUT = 1.4;
const BOW_TIP = 1.25;
const BOW_FOOT = 0.95;
const BOW_SPREAD = 14;

interface Hull {
  x: number;
  z: number;
  elevation: number;
  heading?: number | null;
  footprint: number;
}

/** The four arcs' spans, degrees off the bow: front, right, rear, left (the preset's half-widths fore and aft). */
function spans(preset: ArcPreset): [FacingId, number, number][] {
  const { front, rear } = ARC_PRESETS[preset] ?? ARC_PRESETS.quarters;
  return [
    ['front', -front, front],
    ['right', front, 180 - rear],
    ['rear', 180 - rear, 180 + rear],
    ['left', 180 + rear, 360 - front],
  ];
}

/**
 * Which arcs are lit: the facing the attack will meet, by the app's one rule (`effectiveVehicleFacing`: the GM's
 * pinned choice, else the map's `arcFor`), with a side split by which side of the hull the shooter stands - both
 * sides with no shooter to say which. Nobody to shoot and nothing pinned: no attack is coming, nothing is lit.
 */
function litArcs(
  heading: number,
  shooterBearing: number | null,
  preset: ArcPreset,
  choice?: VehicleFacingChoice | null,
) {
  const pinned = !!choice && choice !== 'auto';
  if (shooterBearing === null && !pinned) return new Set<FacingId>();
  const facing = effectiveVehicleFacing({
    vehicleFacing: choice,
    vehicleFacingFromMap: arcFor(heading, shooterBearing, preset),
  });
  if (facing !== 'side') return new Set<FacingId>([facing]);
  if (shooterBearing === null) return new Set<FacingId>(['right', 'left']);
  return new Set<FacingId>([wrap180(shooterBearing - heading) > 0 ? 'right' : 'left']);
}

/** The bearings off the bow an arc is drawn through, from its start to its end. */
function arcBearings(from: number, to: number): number[] {
  const steps = Math.max(2, Math.round(((to - from) / 90) * STEPS_PER_QUARTER));
  return Array.from({ length: steps + 1 }, (_, i) => from + ((to - from) * i) / steps);
}

/**
 * The hull's facing ring: its four arcs with their armour, the facing in play lit, the bow chevron; null for a hull
 * never turned. `shooter` is the acting figure's place on the ground, or null with none; `choice` the target's own
 * facing choice (a GM's pin, or 'auto').
 */
export function facingRing(
  hull: Hull,
  armour: { front: number; side: number; rear: number },
  shooter: { x: number; z: number } | null,
  preset: ArcPreset,
  choice?: VehicleFacingChoice | null,
): FacingRing | null {
  const heading = hull.heading;
  if (!finite(heading)) return null;
  // A point on the ground `k` radii out, `deg` off the bow: the arcs, their labels and the chevron.
  const at = (deg: number, k: number): Vec3 => ({
    ...headingPoint(hull, heading + deg, hull.footprint * k),
    y: hull.elevation,
  });
  const lit = litArcs(heading, shooter ? bearingBetween(hull, shooter) : null, preset, choice);
  const arcs = spans(preset).map(([id, from, to]) => ({
    id,
    armour: id === 'front' || id === 'rear' ? armour[id] : armour.side,
    lit: lit.has(id),
    points: arcBearings(from, to).map((deg) => at(deg, 1)),
    label: at((from + to) / 2, LABEL_OUT),
  }));
  return { arcs, bow: [at(-BOW_SPREAD, BOW_FOOT), at(0, BOW_TIP), at(BOW_SPREAD, BOW_FOOT)] };
}
